import { useInfiniteQuery } from '@tanstack/react-query';
import BaseService from 'services/api/BaseService';

type SearchParams = {
  search: string;
  url: string;
  additionalParams?: Record<string, any>;
  limit?: number;
};

function useGenericSearch<T extends unknown[]>({
  search,
  url,
  additionalParams = {},
  limit = 10,
}: SearchParams) {
  const query = useInfiniteQuery<T>({
    queryKey: [url, search, additionalParams],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      if (
        !search ||
        Object.values(additionalParams).some((val) => val === undefined)
      ) {
        return [];
      }
      const page = Number(pageParam);
      const offset = (page - 1) * limit;
      const payload = { search, limit, offset, ...additionalParams };
      const response = await BaseService.postRequest(url, payload, true);
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des données');
      }
      return await response.json();
    },
    enabled:
      !!search &&
      Object.values(additionalParams).every((val) => val !== undefined),
    getNextPageParam: (lastPage, allPages) => {
      // No more pages to load
      if (lastPage.length < limit) {
        return undefined;
      }

      // Load the next page
      return allPages.length + 1;
    },
    staleTime: Infinity,
  });

  return {
    data: query.data?.pages.flat() || [],
    isLoading: query.isLoading,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
  };
}

export default useGenericSearch;
